// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — React Query Hooks
// ─────────────────────────────────────────────────────────────────────────────
import {
  useQuery, useMutation, useQueryClient,
} from '@tanstack/react-query';
import { projectsApi, scenariosApi, calculateApi } from '@/lib/api';
import { useAppStore } from '@/store';
import type {
  CreateProjectRequest, UpdateProjectRequest,
  CreateScenarioRequest, UpdateScenarioRequest,
  LCCAParameters,
} from '@lcca-nexus/shared';

// ── Query keys (prevents stale cache issues) ──────────────────────────────────
export const KEYS = {
  projects:          ['projects']                  as const,
  project:    (id: string) => ['projects', id]     as const,
  scenarios:  (pid: string) => ['scenarios', pid]  as const,
  scenario:   (pid: string, id: string) => ['scenarios', pid, id] as const,
  compare:    (pid: string) => ['compare', pid]    as const,
};

// ── Projects ──────────────────────────────────────────────────────────────────

export function useProjects() {
  return useQuery({ queryKey: KEYS.projects, queryFn: projectsApi.list });
}

export function useProject(id: string | null) {
  return useQuery({
    queryKey: KEYS.project(id!),
    queryFn:  () => projectsApi.get(id!),
    enabled:  !!id,
  });
}

export function useCreateProject() {
  const qc     = useQueryClient();
  const notify = useAppStore(s => s.notify);

  return useMutation({
    mutationFn: (body: CreateProjectRequest) => projectsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.projects });
      notify('success', 'Project created!');
    },
    onError: (err: Error) => notify('error', 'Failed to create project', err.message),
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  const notify = useAppStore(s => s.notify);

  return useMutation({
    mutationFn: (body: UpdateProjectRequest) => projectsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.project(id) });
      qc.invalidateQueries({ queryKey: KEYS.projects });
      notify('success', 'Project updated');
    },
    onError: (err: Error) => notify('error', 'Update failed', err.message),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  const notify = useAppStore(s => s.notify);

  return useMutation({
    mutationFn: (id: string) => projectsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.projects });
      notify('success', 'Project deleted');
    },
    onError: (err: Error) => notify('error', 'Delete failed', err.message),
  });
}

// ── Scenarios ─────────────────────────────────────────────────────────────────

export function useScenarios(projectId: string | null) {
  return useQuery({
    queryKey: KEYS.scenarios(projectId!),
    queryFn:  () => scenariosApi.list(projectId!),
    enabled:  !!projectId,
  });
}

export function useScenario(projectId: string | null, id: string | null) {
  return useQuery({
    queryKey: KEYS.scenario(projectId!, id!),
    queryFn:  () => scenariosApi.get(projectId!, id!),
    enabled:  !!projectId && !!id,
  });
}

export function useCreateScenario(projectId: string) {
  const qc = useQueryClient();
  const notify = useAppStore(s => s.notify);

  return useMutation({
    mutationFn: (body: CreateScenarioRequest) => scenariosApi.create(projectId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.scenarios(projectId) });
      qc.invalidateQueries({ queryKey: KEYS.project(projectId) });
      notify('success', 'Scenario saved & calculated!');
    },
    onError: (err: Error) => notify('error', 'Failed to save scenario', err.message),
  });
}

export function useUpdateScenario(projectId: string, id: string) {
  const qc = useQueryClient();
  const notify = useAppStore(s => s.notify);

  return useMutation({
    mutationFn: (body: UpdateScenarioRequest) => scenariosApi.update(projectId, id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.scenarios(projectId) });
      qc.invalidateQueries({ queryKey: KEYS.scenario(projectId, id) });
      notify('success', 'Scenario updated');
    },
    onError: (err: Error) => notify('error', 'Update failed', err.message),
  });
}

export function useDeleteScenario(projectId: string) {
  const qc = useQueryClient();
  const notify = useAppStore(s => s.notify);

  return useMutation({
    mutationFn: (id: string) => scenariosApi.delete(projectId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.scenarios(projectId) });
      notify('success', 'Scenario deleted');
    },
    onError: (err: Error) => notify('error', 'Delete failed', err.message),
  });
}

export function useCompare(projectId: string | null) {
  return useQuery({
    queryKey: KEYS.compare(projectId!),
    queryFn:  () => scenariosApi.compare(projectId!),
    enabled:  !!projectId,
  });
}

// ── Calculate (stateless, no save) ───────────────────────────────────────────

export function useCalculate() {
  const notify = useAppStore(s => s.notify);

  return useMutation({
    mutationFn: (params: LCCAParameters) => calculateApi.calculate(params),
    onError: (err: Error) => notify('error', 'Calculation failed', err.message),
  });
}
